import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { CheckIcon, GripVerticalIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Card } from '../ui/Card';
import { Toggle } from '../ui/Toggle';
import type { PriorityRule } from '../../types';
interface PriorityBuilderProps {
  rules: PriorityRule[];
  onChange: (rules: PriorityRule[]) => void;
}
interface RowProps {
  rule: PriorityRule;
  rank: number | null;
  onUpdate: (patch: Partial<PriorityRule>) => void;
  onDelete: () => void;
}
function PriorityRow({
  rule,
  rank,
  onUpdate,
  onDelete
}: RowProps) {
  const controls = useDragControls();
  const [editing, setEditing] = useState(false);
  return <Reorder.Item value={rule} dragListener={false} dragControls={controls} className={twMerge('flex items-start gap-3 border-b border-ink-200 bg-white px-5 py-4 last:border-b-0', !rule.enabled && 'bg-ink-50/70')} whileDrag={{
    scale: 1.01,
    boxShadow: '0 8px 24px rgba(16,24,40,0.12)',
    zIndex: 10
  }} data-id="element-53" data-mp="1y9o2iqvqpcz5">
      <button type="button" onPointerDown={e => controls.start(e)} className="mt-1 cursor-grab touch-none rounded-md p-1 text-ink-300 transition-colors hover:bg-ink-100 hover:text-ink-500 active:cursor-grabbing" aria-label={`Reorder ${rule.label}`} data-id="element-54" data-mp="2dpwgyhktgkse">
        <GripVerticalIcon className="h-4 w-4" data-id="element-55" data-mp="2so8aifcw7dfc" />
      </button>

      <span className={twMerge('tabular mt-0.5 flex h-7 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold', rule.enabled ? 'bg-ink-900 text-white' : 'bg-ink-200 text-ink-500')} data-id="element-56" data-mp="2slm3qhxtt1y6">
        {rank ? `#${rank}` : '—'}
      </span>

      <div className="min-w-0 flex-1" data-id="element-57" data-mp="36ebut0s7ox25">
        {editing ? <div className="flex items-center gap-2" data-id="element-58" data-mp="3bf9932a3p6yd">
            <input autoFocus value={rule.label} onChange={e => onUpdate({
          label: e.target.value
        })} onKeyDown={e => {
          if (e.key === 'Enter') setEditing(false);
        }} aria-label="Priority rule name" className="h-9 w-full max-w-md rounded-lg border border-brand-300 px-3 text-sm font-medium text-ink-900 outline-none focus:ring-2 focus:ring-brand-100" data-id="element-59" data-mp="3bcd9zamioc8c" />
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg p-2 text-brand-600 transition-colors hover:bg-brand-50" aria-label="Done editing" data-id="element-60" data-mp="2vmxeocjqysdz">
              <CheckIcon className="h-4 w-4" data-id="element-61" data-mp="3jeckifaqfrtz" />
            </button>
          </div> : <p className={twMerge('text-sm font-semibold', rule.enabled ? 'text-ink-900' : 'text-ink-400')} data-id="element-62" data-mp="3eezob3xz18eq">
            {rule.label}
          </p>}
        <p className="mt-1 text-xs leading-relaxed text-ink-500" data-id="element-63" data-mp="1k1gqdt013nsw">{rule.summary}</p>
        <p className="tabular mt-1.5 text-[11px] font-medium text-ink-400" data-id="element-64" data-mp="3vnipebxesnrd">
          {rule.leadCount} leads currently matching
        </p>
      </div>

      <div className="flex items-center gap-1.5" data-id="element-65" data-mp="4gxzwwn05nwg">
        <Toggle checked={rule.enabled} onChange={checked => onUpdate({
        enabled: checked
      })} label={`${rule.enabled ? 'Disable' : 'Enable'} ${rule.label}`} data-id="element-66" data-mp="3lyqy8606a7hi" />
        <button type="button" onClick={() => setEditing(v => !v)} className="rounded-lg p-2 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700" aria-label={`Edit ${rule.label}`} data-id="element-67" data-mp="3cx19x91l7ony">
          <PencilIcon className="h-4 w-4" data-id="element-68" data-mp="3317nmxy2ggsz" />
        </button>
        <button type="button" onClick={onDelete} className="rounded-lg p-2 text-ink-400 transition-colors hover:bg-rose-50 hover:text-rose-600" aria-label={`Delete ${rule.label}`} data-id="element-69" data-mp="37xoehzwnh99r">
          <Trash2Icon className="h-4 w-4" data-id="element-70" data-mp="166l2p7b0xonm" />
        </button>
      </div>
    </Reorder.Item>;
}
export function PriorityBuilder({
  rules,
  onChange
}: PriorityBuilderProps) {
  const enabledIds = rules.filter(r => r.enabled).map(r => r.id);
  const addRule = () => {
    onChange([...rules, {
      id: `p-${Date.now()}`,
      label: 'New priority rule',
      summary: 'Describe which leads this rule captures and when they should be worked.',
      enabled: true,
      leadCount: 0
    }]);
  };
  return <Card className="overflow-hidden" data-id="element-71" data-mp="2ekx4f883utbh">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 px-5 py-4" data-id="element-72" data-mp="2ejvfq0y628ba">
        <div data-id="element-73" data-mp="29m3nf5tm0k6k">
          <h2 className="text-sm font-semibold text-ink-900" data-id="element-74" data-mp="29lu0jr3v2t53">Priority Order</h2>
          <p className="mt-0.5 text-xs text-ink-500" data-id="element-75" data-mp="25ljs87sljqn2">
            Drag to set the exact order Loan Officers work leads
          </p>
        </div>
        <button type="button" onClick={addRule} className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-2 text-sm font-medium text-ink-700 transition-colors hover:border-ink-300 hover:bg-ink-50" data-id="element-76" data-mp="1vlsulx8qovir">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-77" data-mp="g1a5p5bd33of" />
          Add priority rule
        </button>
      </div>

      <div className="border-b border-brand-100 bg-brand-50 px-5 py-3" data-id="element-78" data-mp="kztqv3i4sbfq">
        <p className="text-xs text-brand-800" data-id="element-79" data-mp="p2885j65j34w">
          <span className="font-semibold" data-id="element-80" data-mp="3oin5ljmnb6bh">Worked first:</span>{' '}
          {rules.find(r => r.enabled)?.label ?? 'No active priority rules'}
        </p>
      </div>

      <Reorder.Group axis="y" values={rules} onReorder={onChange} as="ol" data-id="element-81" data-mp="3thznrr2knp7q">
        {rules.map(rule => <PriorityRow key={rule.id} rule={rule} rank={rule.enabled ? enabledIds.indexOf(rule.id) + 1 : null} onUpdate={patch => onChange(rules.map(r => r.id === rule.id ? {
        ...r,
        ...patch
      } : r))} onDelete={() => onChange(rules.filter(r => r.id !== rule.id))} data-id="element-82" data-mp="1buh9vzazabw" />)}
      </Reorder.Group>
    </Card>;
}